import { Controller } from "stimulus";
import Hammer from "hammerjs";
import * as cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";
import cornerstoneMath from "cornerstone-math";
import dicomParser from "dicom-parser";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import * as cornerstoneWebImageLoader from "cornerstone-web-image-loader";

cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
cornerstoneWebImageLoader.external.cornerstone = cornerstone;
cornerstoneTools.external.cornerstone = cornerstone;
cornerstoneTools.external.Hammer = Hammer;
cornerstoneTools.external.cornerstoneMath = cornerstoneMath;

export default class extends Controller {
  static targets = ["viewport", "image", "iframe", "fallback", "scaleLabel"];
  static values = { url: String, kind: String };

  connect() {
    if (this.kindValue === "dicom") {
      this.initDicomViewer();
    } else if (this.kindValue === "image") {
      this.initRasterViewer();
    }
  }

  disconnect() {
    if (this.kindValue === "dicom" && this.viewportTarget.dataset.cornerstoneEnabled === "true") {
      cornerstone.disable(this.viewportTarget);
    }
  }

  initRasterViewer() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.dragging = false;
    this.lastX = 0;
    this.lastY = 0;

    const img = this.imageTarget;
    img.addEventListener("load", () => this.resetView());
    if (img.complete) this.resetView();

    this.viewportTarget.addEventListener("wheel", (event) => this.onWheel(event), { passive: false });
    this.viewportTarget.addEventListener("mousedown", (event) => this.startPan(event));
    window.addEventListener("mousemove", (event) => this.movePan(event));
    window.addEventListener("mouseup", () => this.endPan());
  }

  initDicomViewer() {
    cornerstoneTools.init({
      mouseEnabled: true,
      touchEnabled: true,
      globalToolSyncEnabled: true,
      showSVGCursors: true
    });

    const element = this.viewportTarget;
    cornerstone.enable(element);
    element.dataset.cornerstoneEnabled = "true";

    const { PanTool, ZoomMouseWheelTool, WwwcTool } = cornerstoneTools;
    cornerstoneTools.addTool(PanTool);
    cornerstoneTools.addTool(ZoomMouseWheelTool);
    cornerstoneTools.addTool(WwwcTool);
    cornerstoneTools.setToolActive("Pan", { mouseButtonMask: 1 });
    cornerstoneTools.setToolActive("ZoomMouseWheel", {});
    cornerstoneTools.setToolActive("Wwwc", { mouseButtonMask: 4 });

    const imageId = `wadouri:${this.urlValue}`;
    cornerstone.loadImage(imageId).then((image) => {
      cornerstone.displayImage(element, image);
      cornerstone.fitToWindow(element);
    }).catch(() => {
      this.showFallback("Unable to load DICOM in browser. Download the file to view locally.");
    });
  }

  onWheel(event) {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 1.12 : 0.89;
    this.scale = Math.min(8, Math.max(0.2, this.scale * delta));
    this.applyTransform();
  }

  startPan(event) {
    if (event.button !== 0) return;
    this.dragging = true;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.viewportTarget.classList.add("cursor-grabbing");
  }

  movePan(event) {
    if (!this.dragging) return;
    this.panX += event.clientX - this.lastX;
    this.panY += event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.applyTransform();
  }

  endPan() {
    this.dragging = false;
    this.viewportTarget.classList.remove("cursor-grabbing");
  }

  resetView() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
  }

  zoomIn() {
    this.scale = Math.min(8, this.scale * 1.2);
    this.applyTransform();
  }

  zoomOut() {
    this.scale = Math.max(0.2, this.scale / 1.2);
    this.applyTransform();
  }

  applyTransform() {
    if (!this.hasImageTarget) return;
    this.imageTarget.style.transform =
      `translate(calc(-50% + ${this.panX}px), calc(-50% + ${this.panY}px)) scale(${this.scale})`;
    if (this.hasScaleLabelTarget) {
      this.scaleLabelTarget.textContent = `${Math.round(this.scale * 100)}%`;
    }
  }

  openFullscreen() {
    if (this.kindValue === "pdf" && this.hasIframeTarget) {
      this.iframeTarget.requestFullscreen?.();
      return;
    }
    this.viewportTarget.requestFullscreen?.();
  }

  showFallback(message) {
    if (this.hasFallbackTarget) {
      this.fallbackTarget.textContent = message;
      this.fallbackTarget.classList.remove("hidden");
    }
  }
}
